/**
 * Collapsed-panel RAIL (APP-3397 / APP-3413): a reserved-width bordered column that stands in for the docked
 * conversations panel when it is collapsed. It is a normal flex child (like ChatSidePanel), NOT a floating
 * overlay, so the chat + artifact region lives entirely to its right and can never be underlapped. The rail
 * holds two top-aligned controls: an Expand button that re-docks the full panel (the click/keyboard path to
 * the list, and the caller's focus target), and a New-chat (+) button that starts a thread. Hovering the +
 * button opens a SELECT-ONLY Popper preview of the conversation list (Pinned/Recents) — quick-jump only, no
 * mutation controls and no search. Selecting a row navigates (opening/focusing its tab via the controller) and
 * dismisses the preview WITHOUT re-docking; the Expand button is the explicit re-dock.
 */

import { useCallback, useId, useMemo, useState } from "react"
import type { Ref } from "react"
import { Add, ViewSidebarOutlined } from "@mui/icons-material"
import { Alert, Box, CircularProgress, IconButton, Paper, Popper, Tooltip, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { PANEL_WIDTH, ThreadSection } from "./side-panel"
import type { ThreadRowCallbacks } from "./thread-list-item"
import { partitionThreads } from "./thread-partition"
import { useKellyPanel } from "./kelly-chat-context"
import { useHoverPreview } from "./use-hover-preview"

// A reserved-width column in the panel's border/paper language (mirrors ChatSidePanel's right border). Top
// padding matches the panel's COLLAPSE_ROW (p:0.5) so the Expand button reads as the collapse toggle swapping sides.
const RAIL_SX = { flexShrink: 0, width: (theme: Theme) => theme.spacing(6.5), borderRight: 1, borderColor: "divider", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, p: 0.5, minHeight: 0 } satisfies SxProps<Theme>
const EXPAND_BUTTON_SX = { color: "var(--app-text-secondary)", "&:hover": { color: "var(--app-text-primary)", backgroundColor: "var(--app-subtle-hover-surface)" } } satisfies SxProps<Theme>
// tooltip zIndex keeps the preview above the sticky app bar, like the prior dock.
const POPPER_SX = { zIndex: "tooltip" } satisfies SxProps<Theme>
const PREVIEW_PAPER_SX = { width: PANEL_WIDTH, maxHeight: "70vh", display: "flex", flexDirection: "column", borderRadius: "6px", boxShadow: 8, overflow: "hidden" } satisfies SxProps<Theme>
const LISTS_SCROLL_SX = { flex: 1, overflowY: "auto", minHeight: 0 } satisfies SxProps<Theme>
const ALERT_SX = { m: 1 } satisfies SxProps<Theme>
const LOADING_SX = { display: "flex", justifyContent: "center", p: 2 } satisfies SxProps<Theme>
const HINT_SX = { m: 1, px: 1, py: 0.75, borderRadius: "4px", backgroundColor: "var(--app-subtle-hover-surface)", color: "var(--app-text-secondary)", fontSize: "0.6875rem" } satisfies SxProps<Theme>

type ThreadsStatus = "loading" | "loaded" | "errored"

interface CollapsedRailProps {
  /** Re-dock (expand) the panel. */
  onOpen: () => void
  /** Focus target so a collapse lands focus on the Expand button (mirrors the panel's collapse-button focus). */
  openButtonRef?: Ref<HTMLButtonElement>
}

export function CollapsedRail({ onOpen, openButtonRef }: CollapsedRailProps) {
  const { threads, activeThreadId, isThreadsLoaded, isThreadsErrored, selectThread, startNewThread } = useKellyPanel()
  const { isOpen, surfaceRef, closeNow, triggerHandlers, surfaceHandlers } = useHoverPreview()
  // The + button is both the hover trigger and the Popper anchor; anchor is state so it re-renders when the
  // node mounts (reading a plain ref during render for anchorEl is forbidden — react-hooks/refs).
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const headingId = useId()

  // Select dismisses the preview and navigates; it does not re-dock (the panel stays collapsed — prior behaviour).
  const previewCallbacks = useMemo<ThreadRowCallbacks>(
    () => ({ onSelect: (threadId) => { closeNow(); selectThread(threadId) } }),
    [closeNow, selectThread],
  )
  const handleNewChat = useCallback(() => {
    closeNow()
    startNewThread()
  }, [closeNow, startNewThread])

  const { pinned, recents } = useMemo(() => partitionThreads(threads), [threads])
  // One derived status so the error / loading / list branches below are mutually exclusive (mirrors the side panel's guard).
  const threadsStatus: ThreadsStatus = isThreadsErrored ? "errored" : isThreadsLoaded ? "loaded" : "loading"

  return (
    <Paper square elevation={0} sx={RAIL_SX} component="aside" aria-label="Collapsed conversations panel">
      {/* disableHoverListener: hover belongs to the preview Popper below — a hover tooltip opens in the same
          spot after its delay, sits in the pointer path, and fires the preview's leave handlers so the grace
          timer dismisses the list. Focus/touch still surface the label for keyboard users (no preview then). */}
      <Tooltip title="Open conversations" disableHoverListener>
        <IconButton
          ref={openButtonRef}
          aria-label="Open conversations panel"
          data-testid="chatbox-rail-expand"
          sx={EXPAND_BUTTON_SX}
          onClick={onOpen}
        >
          <ViewSidebarOutlined />
        </IconButton>
      </Tooltip>
      <Tooltip title="New chat">
        <IconButton
          ref={setAnchorEl}
          aria-label="New chat"
          data-testid="chatbox-rail-new-chat"
          color="primary"
          onClick={handleNewChat}
          {...triggerHandlers}
        >
          <Add />
        </IconButton>
      </Tooltip>
      <Popper open={isOpen && anchorEl != null} anchorEl={anchorEl} placement="bottom-start" sx={POPPER_SX}>
        <Paper ref={surfaceRef} sx={PREVIEW_PAPER_SX} data-testid="chatbox-rail-preview" {...surfaceHandlers}>
          {threadsStatus === "errored" && (
            <Alert severity="error" sx={ALERT_SX} data-testid="chatbox-rail-preview-error">
              Couldn&apos;t load your conversations. Please try again later.
            </Alert>
          )}
          {threadsStatus === "loading" && (
            <Box sx={LOADING_SX} data-testid="chatbox-rail-preview-loading">
              <CircularProgress size={20} aria-label="Loading conversations" />
            </Box>
          )}
          {threadsStatus === "loaded" && (
            <Box sx={LISTS_SCROLL_SX}>
              {pinned.length > 0 && (
                <ThreadSection
                  headingId={`${headingId}-pinned`}
                  label="Pinned"
                  threads={pinned}
                  count={pinned.length}
                  activeThreadId={activeThreadId}
                  callbacks={previewCallbacks}
                  variant="preview"
                />
              )}
              {recents.length > 0 && (
                <ThreadSection
                  headingId={`${headingId}-recents`}
                  label="Recents"
                  threads={recents}
                  count={recents.length}
                  activeThreadId={activeThreadId}
                  callbacks={previewCallbacks}
                  variant="preview"
                />
              )}
            </Box>
          )}
          <Typography sx={HINT_SX}>Select a conversation to open it</Typography>
        </Paper>
      </Popper>
    </Paper>
  )
}
